import React from 'react';
import { DiamondIcon, StarIcon, TentIcon, WrenchIcon } from 'lucide-react';
import { yearPlan } from '../data/content';
import { SectionHeading } from './SectionHeading';

/** One color per quarter (full class names so Tailwind keeps them). Dark text on amber for contrast. */
const quarterStyles = [{
  band: 'bg-brand text-white',
  star: 'text-brand'
}, {
  band: 'bg-coral text-white',
  star: 'text-coral-ink'
}, {
  band: 'bg-violet text-white',
  star: 'text-violet-ink'
}, {
  band: 'bg-sun text-ink',
  star: 'text-ink'
}];

/** The 40-meeting plan, quarter by quarter: ★ complete meetings, ▲ outings and camp, ◆ skills checks */
export function YearPlanSection() {
  return <section id="year" aria-labelledby="year-title" className="scroll-mt-4 bg-white py-12 md:py-20" data-id="element-278" data-mp="37blqhpl08zja">
      <div className="mx-auto max-w-page px-5 md:px-8" data-id="element-279" data-mp="29wrbjfqvzzgm">
        <SectionHeading id="year-title" eyebrow={yearPlan.eyebrow} title={yearPlan.title} sub={yearPlan.sub} data-id="element-280" data-mp="29w7us2cz3h46" />

        <ol className="mt-8 grid gap-4 md:mt-12 md:grid-cols-2 lg:grid-cols-4" data-id="element-281" data-mp="2ethrm32oyv21">
          {yearPlan.quarters.map((q, qi) => {
          const s = quarterStyles[qi % quarterStyles.length];
          return <li key={q.q} className="flex flex-col overflow-hidden rounded-xl border border-hair bg-white" data-id="element-282" data-mp="1qjy0idik1mca" data-mpi={qi}>
                <div className={`flex items-baseline justify-between gap-3 px-5 py-3 ${s.band}`} data-id="element-283" data-mp="1ll358c3ei5ba">
                  <span className="font-display text-[1.6rem] font-black leading-none" data-id="element-284" data-mp="1gli9sbnfwf3b">{q.q}</span>
                  <span className="font-display text-[12px] font-extrabold tracking-[0.1em]" data-id="element-285" data-mp="1bmx4hxtbz580">{q.range}</span>
                </div>
                <div className="flex flex-1 flex-col p-5" data-id="element-286" data-mp="17m3hr7rmjwgp">
                  <h3 className="font-display text-[16px] font-black tracking-[0.04em] text-ink" data-id="element-287" data-mp="12lz2tm6d79sl">{q.name}</h3>
                  <ul className="mt-4 space-y-3" data-id="element-288" data-mp="31ytfy1p0zrtw">
                    {q.full.map(m => {
                  const review = m.kind === 'review';
                  const outing = m.kind === 'outing';
                  const Icon = review ? DiamondIcon : outing ? TentIcon : StarIcon;
                  const iconColor = review ? 'text-leaf-ink fill-current' : outing ? 'text-violet-ink' : `${s.star} fill-current`;
                  const titleColor = review ? 'text-leaf-ink' : outing ? 'text-violet-ink' : 'text-brand';
                  return <li key={`${q.q}-${m.n}`} className="flex gap-3" data-id="element-289" data-mp="3c0nieesxnz7k">
                          <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${iconColor}`} strokeWidth={2.25} aria-hidden="true" data-id="element-290" data-mp="3nzsxkqsjzov" />
                          <div className="min-w-0" data-id="element-291" data-mp="7mh1m8pv1i8e">
                            <p className="text-[11px] font-bold tracking-[0.1em] text-ink/55" data-id="element-292" data-mp="3ptmz2k4gbzgp">
                              {yearPlan.unitLabel} {m.n} · {m.label}
                            </p>
                            <p className={`font-display text-[15px] font-bold leading-snug ${titleColor}`} data-id="element-293" data-mp="2ee81be4f53kj">{m.title}</p>
                          </div>
                        </li>;
                })}
                  </ul>
                  <p className="mt-auto pt-4 text-[14px] leading-snug text-ink/70" data-id="element-294" data-mp="2bwo8fg1wmaqy">{q.activities}</p>
                </div>
              </li>;
        })}
        </ol>

        <div className="mx-auto mt-6 max-w-[900px] rounded-xl bg-sun-soft p-5 md:mt-8 md:p-6" data-id="element-295" data-mp="2kuqeia0hvtl3">
          <div className="flex items-center gap-3" data-id="element-296" data-mp="2fx838cz0929x">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sun text-ink" data-id="element-297" data-mp="1s6ch9wh8d1al">
              <WrenchIcon className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" data-id="element-298" data-mp="1o61zznnzmogf" />
            </span>
            <h3 className="font-display text-[14px] font-black tracking-[0.08em] text-ink" data-id="element-299" data-mp="21wxebfh99xre">{yearPlan.toolsTitle}</h3>
          </div>
          <ul className="mt-4 grid gap-2.5 md:grid-cols-3 md:gap-4" data-id="element-300" data-mp="1x4mba3r7xvum">
            {yearPlan.tools.map(t => <li key={t} className="rounded-lg bg-white px-4 py-3 text-[15px] font-medium leading-snug text-ink" data-id="element-301" data-mp="19e9styyckgps">
                {t}
              </li>)}
          </ul>
        </div>
      </div>
    </section>;
}